import { Stack, styled, Typography } from "@mui/material";
import { getDay, getDaysInMonth, getMonth, getYear, subMonths } from "date-fns";
import { HomepageSparklineDay } from "../../../graphql/types";
import { SparklineSquare } from "./Rows";

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const CalendarContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  userSelect: "none",
  width: "100%",
});

const DaysContainer = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(7, 36px)",
  gap: "12px",
  height: "36px",
});

const StatusCalendar = ({
  onClickDay,
  monthNumber,
  selectedDay,
  sparkline,
  year,
}: {
  onClickDay: (day: number, month: number) => void;
  monthNumber: number;
  selectedDay?: number;
  sparkline: HomepageSparklineDay[];
  year: number;
}) => {
  const today = new Date();
  const targetMonth = monthNumber ? monthNumber : getMonth(monthNumber);
  const firstDay = new Date(year, targetMonth, 1);
  const lastDay = new Date(year, targetMonth + 1, 0);
  const daysInMonth = getDaysInMonth(lastDay);
  const firstDayWeek = getDay(firstDay);
  const lastDayWeek = getDay(lastDay);
  const daysInPrevMonth = getDaysInMonth(subMonths(targetMonth, 1));
  const daysPrevMonth: number[] = [];
  const daysNextMonth: number[] = [];

  for (let i = 0; i < firstDayWeek; i++) {
    daysPrevMonth.push(daysInPrevMonth - i);
  }

  for (let i = 1; i < 7 - lastDayWeek; i++) {
    daysNextMonth.push(i);
  }

  const daysCurrentMonth = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <CalendarContainer>
      <DaysContainer>
        {DAYS.map((day, index) => (
          <Typography
            key={`weekdays${index}`}
            variant="body.small.regular"
            sx={{ color: "text30.main", textAlign: "center" }}
          >
            {day}
          </Typography>
        ))}
      </DaysContainer>
      <DaysContainer>
        {daysPrevMonth.reverse().map((day, index) => (
          <Typography
            key={`prevmonthday${index}`}
            variant="body.small.regular"
            sx={{ color: "text20.main", justifySelf: "center", alignSelf: "center" }}
            data-testid={`previous-month-day-${day}`}
          >
            {day}
          </Typography>
        ))}
        {daysCurrentMonth.map((day, index) => (
          <Stack
            key={`currentmonthday${index}`}
            sx={{ width: "36px", height: "36px", justifyContent: "center", alignItems: "center" }}
          >
            {day <= today.getDate() ||
            targetMonth !== getMonth(today) ||
            (targetMonth === getMonth(today) && year !== getYear(today)) ? (
              sparkline[index] && (
                <SparklineSquare
                  isBig
                  key={day}
                  sparkline={sparkline[index] ?? []}
                  day={day.toString()}
                  onClick={() => {
                    onClickDay(day, monthNumber);
                  }}
                  isSelected={selectedDay === day}
                />
              )
            ) : (
              <Typography
                key={`nextmonthday${day}`}
                variant="body.small.regular"
                sx={{ color: "text50.main", justifySelf: "center", alignSelf: "center" }}
              >
                {day}
              </Typography>
            )}
          </Stack>
        ))}
        {daysNextMonth.map((day, index) => (
          <Typography
            key={`nextmonthday${index}`}
            variant="body.small.regular"
            sx={{ color: "text20.main", justifySelf: "center", alignSelf: "center" }}
            data-testid={`next-month-day-${day}`}
          >
            {day}
          </Typography>
        ))}
      </DaysContainer>
    </CalendarContainer>
  );
};

export { StatusCalendar };
